import React, { useState } from "react";
import {
  Package,
  Calendar,
  CreditCard,
  Truck,

  ChevronDown,
  ChevronUp,
} from "lucide-react";


export interface Product {
  name: string;
  price: string;
  quantity: string;
  product_id: string;
  product_sku: string;
  product_image: string;
  principle: string;
}

export interface Order {
  id: number;
  reference_number: string;
  status: string;
  latest_status: string;
  requested_ship_date: string;
  products: Product[];
  grand_total: string;
  total_price: string;
  shipping_price: string;
  date_ordered: string | null;
}


const ProductsList: React.FC<{ products: Product[] }> = ({ products }) => {
  return (
    <div className="mt-4 space-y-3">
      {/* Desktop view for products (hidden on mobile) */}
      <div className="hidden md:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quantity
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Price
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {products.map((product) => (
              <tr key={product.product_id}>
                <td className="px-3 py-4 whitespace-normal">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 mr-4">
                      <img
                        className="w-20 h-20 object-contain bg-gray-50 rounded-lg"
                        src={product.product_image}
                        alt={product.name}
                      />
                    </div>

                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {product.name}
                      </div>
                      {product.principle && (
                        <div className="text-xs text-gray-500">
                          {product.principle}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                  {product.quantity}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                  R{parseFloat(product.price).toFixed(2)}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  R
                  {(
                    parseFloat(product.price) * parseInt(product.quantity)
                  ).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile view for products (shown only on mobile) */}
      <div className="md:hidden space-y-4">
        {products.map((product) => (
          <div
            key={product.product_id}
            className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mr-4">
                <img
                  className="w-20 h-20 object-contain bg-gray-50 rounded-lg"
                  src={product.product_image}
                  alt={product.name}
                />
              </div>

              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  {product.name}
                </div>
                {product.principle && (
                  <div className="text-xs text-gray-500">
                    {product.principle}
                  </div>
                )}
                <div className="mt-2 flex justify-between text-sm">
                  <div className="text-gray-500">
                    <div>Qty: {product.quantity}</div>
                    <div>Price: R{parseFloat(product.price).toFixed(2)}</div>
                  </div>
                  <div className="font-medium text-gray-900">
                    R
                    {(
                      parseFloat(product.price) * parseInt(product.quantity)
                    ).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const getStatusColor = (status: string | null | undefined) => {
  if (!status) {
    return "bg-gray-100 text-gray-800";
  }

  switch (status.toLowerCase()) {
    case "delivered":
      return "bg-green-100 text-green-800";
    case "out for delivery":
      return "bg-emerald-100 text-emerald-800";
    case "in transit":
      return "bg-purple-100 text-purple-800";
    case "collected by courier":
      return "bg-indigo-100 text-indigo-800";
    case "order packed":
      return "bg-blue-100 text-blue-800";
    case "order_received":
      return "bg-sky-100 text-sky-800";
    case "cancelled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const formatStatus = (status: string | null | undefined) => {
  if (!status) {
    return "Unknown";
  }

  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const OrderItem: React.FC<{ order: Order }> = ({ order }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden bg-white">
      {/* Order header (both mobile and desktop) */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-150"
        onClick={() => setIsExpanded(!isExpanded)}>
        {/* Desktop view header */}
        <div className="hidden md:grid md:grid-cols-5 md:gap-4 md:items-center">
          <div className="text-sm font-medium text-gray-900">
            {order.reference_number}
          </div>
          <div className="text-sm text-gray-500">
            {formatDate(order.requested_ship_date)}
          </div>
          <div>
            <span
              className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusColor(
                order.latest_status
              )}`}>
              {formatStatus(order.latest_status)}
            </span>
          </div>
          <div className="text-sm text-gray-900">
            R{parseFloat(order.grand_total).toFixed(2)}
          </div>
          <div className="text-right">
            <button className="text-[#75A7AD] hover:text-[#5a8389] flex items-center ml-auto">
              {isExpanded ? (
                <>
                  <span className="mr-1">Hide Details</span>
                  <ChevronUp size={16} />
                </>
              ) : (
                <>
                  <span className="mr-1">View Details</span>
                  <ChevronDown size={16} />
                </>
              )}
            </button>
          </div>
        </div>

        <div className="md:hidden">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-sm font-medium text-gray-900">
                Order {order.reference_number}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {formatDate(order.requested_ship_date)}
              </div>
            </div>
            <div className="flex flex-col items-end">
              <span
                className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full mb-2 ${getStatusColor(
                  order.latest_status
                )}`}>
                {formatStatus(order.latest_status)}
              </span>
              <div className="text-sm font-medium text-gray-900">
                R{parseFloat(order.grand_total).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <button className="text-[#75A7AD] text-sm flex items-center">
              {isExpanded ? (
                <>
                  <span className="mr-1">Hide Details</span>
                  <ChevronUp size={16} />
                </>
              ) : (
                <>
                  <span className="mr-1">View Details</span>
                  <ChevronDown size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-lg shadow-sm p-4 flex items-center">
              <div className="bg-[#75A7AD] bg-opacity-10 p-3 rounded-full mr-4">
                <Calendar size={20} className="text-[#75A7AD]" />
              </div>
              <div className="flex-1">
                <p className="text-gray-500 text-sm font-medium">Order Date</p>
                <p className="text-gray-800 font-semibold">
                  {formatDate(order.requested_ship_date)}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 flex items-center">
              <div className="bg-[#75A7AD] bg-opacity-10 p-3 rounded-full mr-4">
                <CreditCard size={20} className="text-[#75A7AD]" />
              </div>
              <div className="flex-1">
                <p className="text-gray-500 text-sm font-medium">
                  Total Amount
                </p>
                <p className="text-gray-800 font-semibold">
                  R{parseFloat(order.grand_total).toFixed(2)}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 flex items-center">
              <div className="bg-[#75A7AD] bg-opacity-10 p-3 rounded-full mr-4">
                <Truck size={20} className="text-[#75A7AD]" />
              </div>
              <div className="flex-1">
                <p className="text-gray-500 text-sm font-medium">
                  Delivery Date
                </p>
                <p className="text-gray-800 font-semibold">N/A</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h4 className="font-medium text-gray-700 mb-3">Order Items</h4>
            {order.products && order.products.length > 0 ? (
              <ProductsList products={order.products} />
            ) : (
              <p className="text-gray-500 text-center py-4">
                No products found for this order
              </p>
            )}

            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex justify-end">
                <div className="w-full md:w-64">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Subtotal:</span>
                    <span>R{parseFloat(order.total_price).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Shipping:</span>
                    <span>R{parseFloat(order.shipping_price).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium text-base mt-2 pt-2 border-t border-gray-200">
                    <span>Total:</span>
                    <span>R{parseFloat(order.grand_total).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface MyOrdersProps {
  orders: Order[];
}

const MyOrders: React.FC<MyOrdersProps> = ({ orders }) => {




  if (!orders || orders.length === 0) {
    return (
      <div className="w-full bg-gray-50 rounded-xl overflow-hidden">
        <div className="text-center py-12">
          <div className="bg-[#75A7AD] bg-opacity-10 p-6 rounded-full mx-auto mb-4 w-20 h-20 flex items-center justify-center">
            <Package size={32} className="text-[#75A7AD]" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            No Orders Yet
          </h3>
          <p className="text-gray-500 mb-6">
            You haven't placed any orders yet.
          </p>
          <button className="mt-4 bg-[#75A7AD] text-white py-2 px-6 rounded-md hover:bg-[#5a8389]">
            Start Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-50 p-4 rounded-xl">
      {/* Desktop header - visible only on md screens and up */}
      <div className="hidden md:grid md:grid-cols-5 md:gap-4 p-3 bg-white rounded-lg shadow-sm mb-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
        <div>Order #</div>
        <div>Date</div>
        <div>Status</div>
        <div>Total</div>
        <div className="text-right">Actions</div>
      </div>

      {/* Order items list - responsive for both mobile and desktop */}
      <div className="space-y-4">
        {orders.map((order) => (
          <OrderItem key={order.id} order={order} />
        ))}
      </div>
    </div>
  );
};

export default MyOrders;